import React from 'react';
import './UserGuide.css';

const UserGuide = () => {
  return (
    <div className="guide-container">
      <h1>User Guide</h1>
      <p className="guide-intro">
        This application converts environmental data into MIDI notes in real-time.
        Data points are fetched from an API and transformed into musical events.
      </p>

      <section className="guide-section">
        <h2>Connection</h2>
        <dl className="guide-dl">
          <dt>Start API URL</dt>
          <dd>
            Full URL to the data endpoint, e.g. <code>https://example.com/data?from=2025-01-01T00:00:00&to=...</code>.
            The <code>from</code> parameter sets the playback start time. Click <strong>Apply URL</strong> to parse and apply.
          </dd>

          <dt>baseURL</dt>
          <dd>Server origin extracted from the Start API URL. Used for subsequent data fetches.</dd>

          <dt>startTime</dt>
          <dd>UTC timestamp from the <code>from</code> parameter. Defines "time zero" for playback.</dd>

          <dt>offsetSeconds</dt>
          <dd>
            Seconds between <em>startTime</em> and <em>now</em>. Controls how far into the data stream playback begins.
            Leave blank to auto-compute from current time. Override manually to jump to a specific point.
          </dd>

          <dt>Recompute</dt>
          <dd>Recalculates offsetSeconds based on the current wall-clock time.</dd>
        </dl>
      </section>

      <section className="guide-section">
        <h2>Pitch Mapping</h2>
        <p>Converts the <code>max_height</code> value of each data point into a MIDI note number.</p>
        <dl className="guide-dl">
          <dt>Max height min / max</dt>
          <dd>Expected input range for max_height values. Values outside this range are clamped.</dd>

          <dt>Low note / High note</dt>
          <dd>Output MIDI note range (0–127). Max height min → low note, max height max → high note.</dd>

          <dt>Scale</dt>
          <dd>
            Quantizes pitches to a musical scale. Options: chromatic, major, minor, dorian, phrygian, 
            lydian, mixolydian, locrian, pentatonic. Root is C (MIDI 0, 12, 24…).
          </dd>
        </dl>
      </section>

      <section className="guide-section">
        <h2>Velocity Mapping</h2>
        <p>Converts the height delta (<code>max_height - min_height</code>) of each data point into MIDI velocity.</p>
        <dl className="guide-dl">
          <dt>Min velocity / Max velocity</dt>
          <dd>Output velocity range (0–127). Larger height deltas produce higher velocities.</dd>
        </dl>
      </section>

      <section className="guide-section">
        <h2>Note Length</h2>
        <dl className="guide-dl">
          <dt>Staccato length (ms)</dt>
          <dd>Duration of each note in milliseconds. Note-off is sent after this delay.</dd>

          <dt>Start jitter (ms)</dt>
          <dd>
            Adds a random delay in the range [0, jitter] to each note's start time.
            Creates a looser, more organic feel. Set to 0 for precise timing.
          </dd>
        </dl>
      </section>

      <section className="guide-section">
        <h2>Category Routing</h2>
        <p>
          Each data point belongs to a category (e.g., "Cumulonimbus", "Cirrus"). 
          Route categories to different MIDI ports and channels for multi-timbral setups.
        </p>
        <dl className="guide-dl">
          <dt>Enable checkbox</dt>
          <dd>When unchecked, data points in that category are ignored (no MIDI output).</dd>

          <dt>Port</dt>
          <dd>
            MIDI output port index (1-based). Port 1 = first available output, Port 2 = second, etc.
            Ensure the corresponding IAC Driver or hardware port is configured.
          </dd>

          <dt>Channel</dt>
          <dd>MIDI channel (1–16). Use different channels to separate instrument layers in your DAW.</dd>

          <dt>Select all / Select none</dt>
          <dd>Bulk enable or disable all categories.</dd>
        </dl>
      </section>

      <section className="guide-section">
        <h2>Playback Controls</h2>
        <dl className="guide-dl">
          <dt>Start</dt>
          <dd>Begins fetching data and scheduling MIDI notes. Settings are locked while playing.</dd>

          <dt>Stop</dt>
          <dd>Stops playback, clears scheduled notes, and sends "all notes off" to silence any held notes.</dd>

          <dt>Status badge</dt>
          <dd>Shows "Playing" or "Stopped" to indicate current state.</dd>
        </dl>
      </section>

      <section className="guide-section">
        <h2>Typical Workflow</h2>
        <ol className="guide-ol">
          <li>Configure MIDI ports in <strong>Midi Setup</strong> tab.</li>
          <li>Paste a data API URL in <strong>Start API URL</strong> and click <strong>Apply URL</strong>.</li>
          <li>Adjust pitch, velocity, and note length to taste.</li>
          <li>Enable desired categories and assign ports/channels.</li>
          <li>Arm MIDI tracks in your DAW, then click <strong>Start</strong>.</li>
          <li>Click <strong>Stop</strong> when finished.</li>
        </ol>
      </section>
    </div>
  );
};

export default UserGuide;

